import { FORM_ERROR, getDRFFormError, useForm, useFormState, type SubmissionErrors } from '@ff-it/form';
import type { RequestFailure } from '@ff-it/api';
import { formatError } from 'utilities';
import { Box } from '@ff-it/ui';
import type { ReactElement } from 'react';
import type { ActivityType } from 'types';
import { DiscountRuleConditions, DiscountRuleDialogShell, DiscountRuleValues } from 'modules/agreement/discountTree';
import { clientTargetInvalidReason } from './clientTarget';
import { clientDiscountOwner, targetBelongsToOwner, type ClientDiscountOwner } from './owner';
import { CompanyField } from 'modules/core/company/components';
import type { ClientDiscountRootOwner } from '../vendorTerms/types';
import { ClientTargetPicker } from './ClientTargetPicker';
import { ruleValueSides, validateRule } from './form';
import type { ClientDiscountSaveInput, ClientRuleFormValues, ClientTargetPath } from './types';
import * as dialogStyles from 'modules/agreement/discountTree/DiscountRuleDialog.css';

type ClientRuleMutationResult = SubmissionErrors | void;
export type ClientRuleMutation = (rule: ClientDiscountSaveInput) => Promise<ClientRuleMutationResult>;

function ownerFromPath(path: ClientTargetPath): ClientDiscountOwner {
  const root = path[0];
  return clientDiscountOwner(root.kind === 'vendor' ? root : { ...root, type: root.type ?? null });
}

function ClientRuleFields({
  activeType,
  owner,
  kind,
}: {
  activeType: ActivityType;
  owner?: ClientDiscountOwner;
  kind: ClientDiscountRootOwner['kind'];
}) {
  const form = useForm<ClientRuleFormValues>();
  const { values } = useFormState<ClientRuleFormValues>({
    subscription: { values: true },
  });
  const selectedPath = values.target_path;
  const targetInvalid = selectedPath ? clientTargetInvalidReason(selectedPath, activeType) : null;
  const ownerInvalid = owner && selectedPath != null && !targetBelongsToOwner(selectedPath, owner);

  return (
    <>
      {(targetInvalid || ownerInvalid) && (
        <div className={dialogStyles.invalidNotice} role="alert">
          {ownerInvalid
            ? `This target no longer belongs to ${owner!.name}. This rule does not apply. Select a target within this section or delete it.`
            : `This target does not match ${activeType}. Select a compatible target before saving.`}
        </div>
      )}
      <div className={dialogStyles.layout}>
        <Box minWidth="0">
          <ClientTargetPicker
            type={activeType}
            owner={owner}
            kind={kind}
            value={selectedPath}
            onChange={(path) => form.change('target_path', path)}
          />
          <Box textSize="xs" color="secondary" mt="3">
            {owner?.targetHint ??
              (kind === 'vendor'
                ? 'Choose a vendor, outlet, or media product.'
                : 'Choose a service category or service product.')}
          </Box>
        </Box>
        <Box minWidth="0" display="flex" flexDirection="column" gap="2">
          {selectedPath?.length === 1 && (
            <CompanyField
              name="provider"
              label="Provider"
              size="sm"
              includeBranches={false}
              isClearable
              menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
              menuPosition="fixed"
              help="Applies to every rule under this root."
            />
          )}
          <DiscountRuleValues
            activeType={activeType}
            sides={ruleValueSides}
            productKind={kind === 'vendor' ? 'MEDIA' : 'SERVICE'}
            hideIncompatible
          />
          <DiscountRuleConditions
            activityKind={kind === 'vendor' ? 'MEDIA' : 'SERVICE'}
            activityLabel="Activity condition"
            categoryLabel="Category condition"
            termsHelp={
              <>
                Each side resolves independently. Blank values use the next matching rule; zero is an explicit value.{' '}
                {kind === 'service-category'
                  ? 'Without a matching service provider value, no provider discount is supplied.'
                  : 'Without a matching media provider value, the provider agreement applies.'}
              </>
            }
          />
        </Box>
      </div>
    </>
  );
}

export type ClientRuleDialogProps = {
  open: boolean;
  activeType: ActivityType;
  owner?: ClientDiscountOwner;
  kind?: ClientDiscountRootOwner['kind'];
  draft: ClientRuleFormValues;
  editingRuleId?: number;
  onSubmit: ClientRuleMutation;
  onRemove?: () => Promise<void>;
  removeLabel?: string;
  onClose: () => void;
};

export function ClientRuleDialog({
  open,
  activeType,
  owner,
  kind = owner?.kind ?? 'vendor',
  draft,
  editingRuleId,
  onSubmit,
  onRemove,
  removeLabel,
  onClose,
}: ClientRuleDialogProps): ReactElement {
  const title = `${editingRuleId !== undefined ? 'Edit' : 'Add'} discount rule`;

  return (
    <DiscountRuleDialogShell<ClientRuleFormValues>
      open={open}
      title={title}
      initialValues={{ ...draft, provider: draft.provider ?? null }}
      validate={(values) =>
        validateRule(
          values,
          activeType,
          owner ?? (values.target_path ? ownerFromPath(values.target_path) : { kind, id: 0 }),
          editingRuleId === undefined,
        )
      }
      onSubmit={async (values) => {
        if (!values.target_path) return;
        try {
          const { provider, ...rule } = values;
          return await onSubmit({
            ...rule,
            target_path: values.target_path,
            ...(values.target_path.length === 1 ? { provider: provider ?? null } : {}),
          });
        } catch (error) {
          const errors = getDRFFormError(error as RequestFailure<unknown>);
          if (errors?.target) return { ...errors, [FORM_ERROR]: formatError({ data: errors.target }) };
          throw error;
        }
      }}
      onRemove={onRemove}
      removeLabel={removeLabel}
      onClose={onClose}
    >
      <ClientRuleFields activeType={activeType} owner={owner} kind={kind} />
    </DiscountRuleDialogShell>
  );
}
